
  export default {
    mounted() {
      const chartData = JSON.parse(this.el.dataset.chartData)
      const areaUnit = this.el.dataset.areaUnit
      const i18n = JSON.parse(this.el.dataset.i18n || "null") || window.I18n || {}
      if (!areaUnit) {
        throw new Error(
          "data-area-unit attribute is required on CriticalLocationsChart element"
        )
      }
      this.renderChart(chartData, areaUnit, i18n)

      // Listen for data updates via push_event (since we use phx-update="ignore")
      this.handleEvent("update-critical-locations-chart", ({ data }) => {
        this.renderChart(data, areaUnit, i18n)
      })
    },
    renderChart(chartData, areaUnit, i18n) {
      const hook = this
      const t = (key) => i18n[key]
      const getLocale = () => i18n?.numberFormat?.locale || "en"
      const dateFormatter = new Intl.DateTimeFormat(getLocale(), { year: "numeric", month: "2-digit", day: "2-digit" })
      const monthsMargin = 3
      const monthsMarkers = [12, 18, 24, 36, 48]
      const riskOrder = ["red", "orange", "yellow", "green"]

      // areaUnit is now the abbreviation (e.g., "sf", "m²")
      const unitLabel = areaUnit

      const colorMap = {
        todayMarker: {
          line: "#3b82f6",
          label: "#64748b",
        },
        monthMarker: {
          line: "#94a3b8",
          label: "#64748b",
        },
        risk: {
          green: "#0086e6",
          yellow: "#F97316",
          orange: "#F97316",
          red: "#E11D48",
        },
      }

      const byRisk = {
        green: { name: t("Safe"), data: [], color: colorMap.risk.green },
        yellow: { name: t("Monitor"), data: [], color: colorMap.risk.yellow },
        orange: { name: t("Warning"), data: [], color: colorMap.risk.orange },
        red: { name: t("Critical"), data: [], color: colorMap.risk.red },
      }

      const today = new Date()
      today.setUTCHours(0, 0, 0, 0)
      let maxDate = new Date(today)
      let minDate = new Date(today)

      chartData.forEach((item, i) => {
        const expirationDate = new Date(item.expires_at)

        if (expirationDate > maxDate) {
          maxDate = expirationDate
        }

        if (expirationDate < minDate) {
          minDate = expirationDate
        }

        const sizeInSf = parseInt(item.size_in_sf) || 0
        const point = {
          x: expirationDate.getTime(),
          y: sizeInSf,
          z: sizeInSf,
          id: item.id,
          size: parseInt(item.size) || 0,
          size_in_sf: sizeInSf,
          area_unit_abbr: item.area_unit_abbr,
          months_until_expiration: item.months_until_expiration,
          name: item.location.property.place.name,
          expires_at: item.expires_at,
          is_critical: item.is_critical,
          required_notice_months: item.required_notice_months,
          city: item.city,
          location_state: item.state,
          product_type: item.product_type,
          notice_required_by: item.notice_required_by,
          date_type_labels: item.date_type_labels || [],
        }

        byRisk[item.risk_level].data.push(point)
      })

      const series = riskOrder
        .map((r) => byRisk[r])
        .filter((s) => s.data.length > 0)

      const verticalLines = monthsMarkers
        .map((months) => {
          const markerDate = new Date(today)
          markerDate.setMonth(markerDate.getMonth() + months)

          if (markerDate <= maxDate) {
            return {
              color: colorMap.monthMarker.line,
              dashStyle: "dot",
              width: 1,
              value: markerDate.getTime(),
              label: {
                text: `${months} ${t("mo")}`,
                style: { color: colorMap.monthMarker.label, fontSize: "8px" },
              },
            }
          }

          return null
        })
        .filter((line) => line !== null)

      minDate.setMonth(minDate.getMonth() - monthsMargin)
      maxDate.setMonth(maxDate.getMonth() + monthsMargin)

      verticalLines.unshift({
        color: colorMap.todayMarker.line,
        dashStyle: "dash",
        width: 1,
        value: today.getTime(),
        label: {
          text: t("Today"),
          style: { color: colorMap.todayMarker.label, fontSize: "8px" },
        },
      })

      import("highcharts").then((Highcharts) => {
        window.Enaia.patchTooltipDelay(Highcharts)
        window.Enaia.applyHighchartsTheme(Highcharts)
        Highcharts.chart(this.el.id, {
          credits: { enabled: false },
          title: { text: null },
          chart: {
            type: "bubble",
            // Highcharts 13 defaults chart.backgroundColor to a CSS var that
            // renders dark under prefers-color-scheme: dark; keep it explicit.
            backgroundColor: "transparent",
            plotBorderWidth: 1,
            zooming: {
              type: "xy",
            },
          },

          xAxis: {
            type: "datetime",
            gridLineWidth: 1,
            title: {
              text: null,
            },
            min: minDate.getTime(),
            max: maxDate.getTime(),
            labels: {
              formatter: function () {
                const locale = getLocale()
                return new Intl.DateTimeFormat(locale, { month: "short", year: "2-digit" }).format(this.value)
              },
            },
            plotLines: verticalLines,
          },

          yAxis: {
            startOnTick: false,
            endOnTick: false,
            gridLineWidth: 1,
            title: {
              text: `${t("Size")} (${unitLabel})`,
            },
          },

          legend: {
            enabled: true,
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
            itemStyle: {
              fontSize: "14px",
              fontWeight: "500",
            },
            itemMarginRight: 24,
          },

          tooltip: {
            delayForDisplay: 500,
            useHTML: true,
            outside: true,
            backgroundColor: 'white',
            borderColor: 'rgba(138,148,153,0.5)',
            borderRadius: 4,
            borderWidth: 1,
            shadow: {
              offsetX: 2,
              offsetY: 4,
              width: 4,
              color: 'rgba(0,0,0,0.25)'
            },
            style: {
              padding: '0'
            },
            followPointer: true,
            formatter: function () {
              const point = this.point
              const bubbleColor = point.color || '#E11D48'
              const name = point.name || t("Unknown Property")
              const cityState = [point.city, point.location_state].filter(Boolean).join(', ')
              const expiresAt = point.expires_at ? dateFormatter.format(new Date(point.expires_at)) : t("N/A")
              const noticeRequiredBy = point.notice_required_by ? dateFormatter.format(new Date(point.notice_required_by)) : t("N/A")
              const formattedSize = point.size ? new Intl.NumberFormat().format(point.size) + ' ' + (point.area_unit_abbr || unitLabel) : ''
              const productType = point.product_type || ''
              const sizeType = [formattedSize, productType].filter(Boolean).join(' | ')

              const labels = point.date_type_labels || []
              const dateTypeHeader = labels.length > 0 ? labels.join(' / ') : t("Lease Expiration Date")
              const dateLabel = t("Expires on")

              return `<div style="padding: 12px 16px;">
                <div style="color: ${bubbleColor}; font-size: 14px; font-weight: 500; margin-bottom: 12px;">
                  ${dateTypeHeader}
                </div>
                <div style="margin-bottom: 0;">
                  <div style="font-size: 16px; font-weight: 600; color: #111827; margin-bottom: 2px;">
                    ${name}
                  </div>
                  ${cityState ? `<div style="font-size: 13px; color: #9CA3AF; margin-bottom: 8px;">${cityState}</div>` : ''}
                  <div style="font-size: 13px; color: #6B7280; margin-bottom: 2px;">${dateLabel} ${expiresAt}</div>
                  <div style="font-size: 13px; color: #6B7280; margin-bottom: 2px;">${t("Notice required by")} ${noticeRequiredBy}</div>
                  ${sizeType ? `<div style="font-size: 13px; color: #6B7280;">${sizeType}</div>` : ''}
                </div>
              </div>`
            }
          },

          plotOptions: {
            bubble: {
              minSize: 40,
              maxSize: 80,
              marker: {
                lineWidth: 0,
                lineColor: null,
              },
            },
            series: {
              cursor: "pointer",
              dataLabels: {
                enabled: true,
                inside: true,
                verticalAlign: "middle",
                style: {
                  color: "#ffffff",
                  fontSize: "11px",
                  fontWeight: "600",
                  textOutline: "none",
                },
                formatter: function () {
                  const size = this.point.size_in_sf || this.point.size
                  if (size === 0) return null
                  if (size >= 1000000) {
                    return (size / 1000000).toFixed(1) + "M"
                  }
                  if (size >= 1000) {
                    return Math.round(size / 1000) + "k"
                  }
                  return new Intl.NumberFormat().format(size)
                },
              },
              point: {
                events: {
                  click: (event) => {
                    hook.pushEvent("show_details", {
                      id: event.point.id,
                    })
                  },
                },
              },
            },
          },

          series: series,
        })
      })
    },
  }
